import type { Edge, Node } from 'reactflow';
import type { TriggerNodeData } from './TriggerNode/TriggerNode';
import {
  buildTriggerPreview,
  defaultTriggerSettings,
  type TriggerSettings,
} from './triggerWorkflowTypes';
import { defaultLlmSettings, type FlowLlmNodePersistedData, type McpStep } from './llmWorkflowTypes';
import { createAgentDataForKind, type FlowAgentPersistedData } from './agentWorkflowTypes';
import { createDefaultActionData, type FlowActionPersistedData } from './actionWorkflowTypes';
import { createDefaultFlowFxData, type FlowFxPersistedData } from './flowFxWorkflowTypes';

/** Serializable trigger node data (matches canvas + config panel). */
export type DemoFlowTriggerData = TriggerNodeData & { settings: TriggerSettings };

export type DemoFlowNodeData =
  | DemoFlowTriggerData
  | FlowLlmNodePersistedData
  | FlowAgentPersistedData
  | FlowActionPersistedData
  | FlowFxPersistedData;

/** Horizontal spine + branch Y offsets — keeps nodes from overlapping. */
const Y_MAIN = 300;
const Y_QUAL = 110;
const Y_NURTURE = 520;
const Y_PAR_UP = 20;
const Y_PAR_DN = 220;

const x = (step: number) => step * 300;

/**
 * Long demo: webhook → normalize → LLM (MCPs) → emit → transform → if/else
 * • TRUE: agent → HTTP (calendar) → parallel (CRM + ops email) → merge
 * • FALSE: wait → Slack
 * → final merge → logger → end
 * + two comment callouts (documentation only).
 */
export function buildComplexDemoWorkflow(): {
  nodes: Node<DemoFlowNodeData>[];
  edges: Edge[];
} {
  const webhookSettings = defaultTriggerSettings('webhook');
  if (webhookSettings.kind === 'webhook') {
    webhookSettings.path = '/webhooks/sales/inbound-lead';
    webhookSettings.auth = 'hmac';
  }
  const triggerKind = 'webhook' as const;
  const triggerData: DemoFlowTriggerData = {
    label: 'Inbound Lead Webhook',
    triggerKind,
    preview: buildTriggerPreview(triggerKind, webhookSettings),
    settings: webhookSettings,
  };

  const setVar = createDefaultFlowFxData({ type: 'Util_SetVariable', label: 'Normalize payload' });
  if (setVar) {
    setVar.nodeName = 'normalize_lead';
    setVar.setVariableRows = [
      { id: 'sv-1', name: 'source', value: '{{ trigger.payload.source }}' },
      { id: 'sv-2', name: 'email', value: '{{ trigger.payload.email }}' },
      { id: 'sv-3', name: 'company_domain', value: '{{ trigger.payload.company_domain }}' },
      { id: 'sv-4', name: 'intent_score', value: '{{ trigger.payload.intent_score }}' },
      { id: 'sv-5', name: 'region', value: '{{ trigger.payload.region }}' },
    ];
  }

  const commentEnrich = createDefaultFlowFxData({ type: 'Util_Comment', label: 'Enrichment stage' });
  if (commentEnrich) {
    commentEnrich.commentTitle = 'Enrichment & MCP';
    commentEnrich.commentBody =
      'LLM pulls CRM row + playbook KB. Downstream gates use JSON score from model output.';
    commentEnrich.commentAccent = 'blue';
  }

  const commentParallel = createDefaultFlowFxData({ type: 'Util_Comment', label: 'Parallel fan-out' });
  if (commentParallel) {
    commentParallel.commentTitle = 'CRM + Ops notify';
    commentParallel.commentBody =
      'Both branches run after calendar hold is placed. Rejoin before merging with nurture path.';
    commentParallel.commentAccent = 'amber';
  }

  const llmBase = defaultLlmSettings('Lead Enrichment Brain', 'LLM_GPT4o');
  const crmStep: McpStep = {
    id: 'demo-mcp-crm',
    kind: 'database',
    label: 'CRM Leads',
    outputKey: 'crm_record',
    paletteType: 'MCP_CRM_Leads',
    database: {
      table: 'leads',
      query: 'lookup by email = {{ normalize_lead.email }}',
      operation: 'read',
    },
  };
  const kbStep: McpStep = {
    id: 'demo-mcp-kb',
    kind: 'knowledge_base',
    label: 'Playbooks',
    outputKey: 'playbook_context',
    paletteType: 'MCP_KB_Sales',
    knowledgeBase: {
      kbName: 'enterprise_sales_playbooks',
      queryTemplate: '{{ normalize_lead.company_domain }} buying signals',
    },
  };
  const llmData: FlowLlmNodePersistedData = {
    label: 'Enrichment LLM',
    paletteType: 'LLM_GPT4o',
    ...llmBase,
    nodeName: 'enrichment_llm',
    operation: 'Analyze',
    parentPrompt:
      'You are a RevOps analyst. Merge webhook payload with CRM row and playbook snippets.\n' +
      'Return JSON: { score, grade, reasoning, recommended_owner }.\n\n' +
      'Payload: {{ trigger.payload }}\nCRM: {{ enrichment_llm.crm_record }}\nKB: {{ enrichment_llm.playbook_context }}',
    mcpSteps: [crmStep, kbStep],
    temperature: 0.2,
    maxTokens: 3500,
  };

  const emitLead = createDefaultActionData({ type: 'Action_EmitEvent', label: 'Emit lead.enriched' });
  if (emitLead) {
    emitLead.nodeName = 'emit_enriched';
    emitLead.emitEventName = 'lead.enriched';
    emitLead.emitPayload = `{
  "email": "{{ normalize_lead.email }}",
  "score": "{{ enrichment_llm.output.score }}",
  "grade": "{{ enrichment_llm.output.grade }}"
}`;
  }

  const shapeLead = createDefaultFlowFxData({ type: 'Util_TransformData', label: 'Shape for router' });
  if (shapeLead) {
    shapeLead.nodeName = 'shape_lead';
    shapeLead.transformInputExpr = '{{ enrichment_llm.output }}';
    shapeLead.transformType = 'map';
    shapeLead.transformExpr = `(o) => ({
  score: Number(o.score ?? 0),
  route: o.grade === 'A' || o.grade === 'B' ? 'fast_lane' : 'standard',
  owner: o.recommended_owner
})`;
  }

  const gate = createDefaultFlowFxData({ type: 'Flow_IfElse', label: 'Score ≥ 75?' });
  if (gate) {
    gate.nodeName = 'score_gate';
    gate.ifConditionExpr = '{{ shape_lead.output.score }} >= 75';
    gate.ifTrueLabel = 'Qualified';
    gate.ifFalseLabel = 'Nurture';
  }

  const agentData = createAgentDataForKind('data-analyst', {
    type: 'Agent_DataAnalyst',
    label: 'Qualification Agent',
  });
  agentData.nodeName = 'qualification_agent';
  agentData.objective =
    'Produce AE-ready briefing: fit summary, risks, suggested next step, and talk track bullets.';
  agentData.inputMappingOverride = '{{ shape_lead.output }}';

  const httpCalendar = createDefaultActionData({ type: 'Action_HttpRequest', label: 'Hold AE calendar' });
  if (httpCalendar) {
    httpCalendar.nodeName = 'calendar_hold';
    httpCalendar.httpMethod = 'POST';
    httpCalendar.httpUrl = 'https://api.calendar.example.com/v1/holds';
    httpCalendar.httpBody = `{
  "owner": "{{ enrichment_llm.output.recommended_owner }}",
  "lead": "{{ normalize_lead.email }}",
  "duration_min": 30
}`;
  }

  const parallel = createDefaultFlowFxData({ type: 'Flow_Parallel', label: 'Parallel handoffs' });
  if (parallel) {
    parallel.nodeName = 'parallel_handoffs';
    parallel.parallelBranchCount = 2;
    parallel.parallelLabels = ['Sync CRM', 'Ops email'];
  }

  const updateCrm = createDefaultActionData({ type: 'Action_UpdateRecord', label: 'Update CRM' });
  if (updateCrm) {
    updateCrm.nodeName = 'crm_update';
    updateCrm.crmEntity = 'Lead';
    updateCrm.crmRecordId = '{{ normalize_lead.email }}';
    updateCrm.crmFields = [
      { id: 'cf1', key: 'lifecycle_stage', value: '"qualified"' },
      { id: 'cf2', key: 'last_score', value: '{{ enrichment_llm.output.score }}' },
      { id: 'cf3', key: 'owner_id', value: '{{ enrichment_llm.output.recommended_owner }}' },
    ];
  }

  const opsEmail = createDefaultActionData({ type: 'Action_SendEmail', label: 'Ops distribution' });
  if (opsEmail) {
    opsEmail.nodeName = 'ops_notify';
    opsEmail.emailToLines = ['sales-ops@example.com'];
    opsEmail.emailSubject = '[Qualified] {{ normalize_lead.company_domain }}';
    opsEmail.emailBody =
      'New qualified lead.\n\nAgent output: {{ qualification_agent.report }}\nCRM sync queued on parallel path A.';
  }

  const mergeParallel = createDefaultFlowFxData({ type: 'Flow_Merge', label: 'Join parallel' });
  if (mergeParallel) {
    mergeParallel.nodeName = 'merge_parallel';
    mergeParallel.mergeInputCount = 2;
    mergeParallel.mergeStrategy = 'all';
    mergeParallel.mergeTimeoutSec = '90';
    mergeParallel.mergeOnTimeout = 'continue';
  }

  const waitNurture = createDefaultActionData({ type: 'Action_WaitDelay', label: 'Cooldown' });
  if (waitNurture) {
    waitNurture.nodeName = 'nurture_wait';
    waitNurture.waitDelayType = 'duration';
    waitNurture.waitDuration = '2';
    waitNurture.waitUnit = 'minutes';
  }

  const slackNurture = createDefaultActionData({ type: 'Action_SendSlack', label: 'Nurture Slack' });
  if (slackNurture) {
    slackNurture.nodeName = 'slack_nurture';
    slackNurture.slackChannel = '#nurture-pipeline';
    slackNurture.slackMessage =
      '*Nurture track* — {{ normalize_lead.email }} ({{ normalize_lead.company_domain }})\n' +
      'Score: {{ enrichment_llm.output.score }} — _{{ enrichment_llm.output.reasoning }}_';
  }

  const mergeFinal = createDefaultFlowFxData({ type: 'Flow_Merge', label: 'Merge all paths' });
  if (mergeFinal) {
    mergeFinal.nodeName = 'merge_all_paths';
    mergeFinal.mergeInputCount = 2;
    mergeFinal.mergeStrategy = 'any';
    mergeFinal.mergeTimeoutSec = '180';
    mergeFinal.mergeOnTimeout = 'continue';
  }

  const notifyAe = createDefaultActionData({ type: 'Action_SendEmail', label: 'AE handoff' });
  if (notifyAe) {
    notifyAe.nodeName = 'ae_handoff_email';
    notifyAe.emailToLines = ['{{ enrichment_llm.output.recommended_owner }}'];
    notifyAe.emailSubject = 'Hot lead: {{ normalize_lead.company_domain }}';
    notifyAe.emailBody =
      'Hi,\n\nQualified lead ready.\n\nBrief: {{ qualification_agent.report }}\nCalendar: {{ calendar_hold.result }}\n';
  }

  const auditLog = createDefaultFlowFxData({ type: 'Util_Logger', label: 'Audit trail' });
  if (auditLog) {
    auditLog.nodeName = 'audit_log';
    auditLog.utilLogLevel = 'info';
    auditLog.utilLogMessage =
      'Pipeline exit for {{ normalize_lead.email }} — merged: {{ merge_all_paths.completed }}';
    auditLog.utilLogPayload = true;
    auditLog.utilLogTimestamp = true;
    auditLog.utilLogNodePath = true;
  }

  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Workflow complete' });
  if (end) {
    end.nodeName = 'workflow_end';
    end.endReturnKind = 'custom';
    end.endCustomExpr = `{
  "lead": "{{ normalize_lead.email }}",
  "score": "{{ enrichment_llm.output.score }}",
  "merged": "{{ merge_all_paths.results }}",
  "status": "done"
}`;
    end.endStatus = 'success';
  }

  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'demo-trigger', type: 'triggerNode', position: { x: x(0), y: Y_MAIN }, data: triggerData },
    { id: 'demo-set-vars', type: 'flowFxNode', position: { x: x(1), y: Y_MAIN }, data: setVar! },
    {
      id: 'demo-cmt-enrich',
      type: 'flowFxNode',
      position: { x: x(1) + 40, y: 40 },
      data: commentEnrich!,
    },
    { id: 'demo-llm', type: 'llmNode', position: { x: x(2), y: Y_MAIN - 30 }, data: llmData },
    { id: 'demo-emit', type: 'actionNode', position: { x: x(3), y: Y_MAIN }, data: emitLead! },
    { id: 'demo-transform', type: 'flowFxNode', position: { x: x(4), y: Y_MAIN }, data: shapeLead! },
    { id: 'demo-gate', type: 'flowFxNode', position: { x: x(5), y: Y_MAIN }, data: gate! },
    { id: 'demo-agent', type: 'agentNode', position: { x: x(6), y: Y_QUAL }, data: agentData },
    { id: 'demo-ae-email', type: 'actionNode', position: { x: x(7), y: Y_QUAL + 85 }, data: notifyAe! },
    { id: 'demo-http-cal', type: 'actionNode', position: { x: x(8), y: Y_QUAL }, data: httpCalendar! },
    { id: 'demo-parallel', type: 'flowFxNode', position: { x: x(9), y: Y_QUAL }, data: parallel! },
    {
      id: 'demo-cmt-parallel',
      type: 'flowFxNode',
      position: { x: x(8) + 30, y: 28 },
      data: commentParallel!,
    },
    { id: 'demo-crm', type: 'actionNode', position: { x: x(10), y: Y_PAR_UP }, data: updateCrm! },
    { id: 'demo-ops-email', type: 'actionNode', position: { x: x(10), y: Y_PAR_DN }, data: opsEmail! },
    { id: 'demo-merge-p', type: 'flowFxNode', position: { x: x(11), y: Y_QUAL }, data: mergeParallel! },
    { id: 'demo-wait', type: 'actionNode', position: { x: x(6), y: Y_NURTURE }, data: waitNurture! },
    { id: 'demo-slack', type: 'actionNode', position: { x: x(7), y: Y_NURTURE }, data: slackNurture! },
    { id: 'demo-merge-f', type: 'flowFxNode', position: { x: x(12), y: Y_MAIN }, data: mergeFinal! },
    { id: 'demo-logger', type: 'flowFxNode', position: { x: x(13), y: Y_MAIN }, data: auditLog! },
    { id: 'demo-end', type: 'flowFxNode', position: { x: x(14), y: Y_MAIN }, data: end! },
  ];

  const edges: Edge[] = [
    mk('e-t-s', 'demo-trigger', 'out', 'demo-set-vars', 'in'),
    mk('e-s-l', 'demo-set-vars', 'out', 'demo-llm', 'in'),
    mk('e-l-e', 'demo-llm', 'out', 'demo-emit', 'in'),
    mk('e-e-x', 'demo-emit', 'out', 'demo-transform', 'in'),
    mk('e-x-g', 'demo-transform', 'out', 'demo-gate', 'in'),
    mk('e-g-a', 'demo-gate', 'out-true', 'demo-agent', 'in'),
    mk('e-g-w', 'demo-gate', 'out-false', 'demo-wait', 'in'),
    mk('e-a-ae', 'demo-agent', 'out', 'demo-ae-email', 'in'),
    mk('e-ae-h', 'demo-ae-email', 'out', 'demo-http-cal', 'in'),
    mk('e-h-p', 'demo-http-cal', 'out', 'demo-parallel', 'in'),
    mk('e-p-c', 'demo-parallel', 'out-path-0', 'demo-crm', 'in'),
    mk('e-p-o', 'demo-parallel', 'out-path-1', 'demo-ops-email', 'in'),
    mk('e-c-mp0', 'demo-crm', 'out', 'demo-merge-p', 'in-0'),
    mk('e-o-mp1', 'demo-ops-email', 'out', 'demo-merge-p', 'in-1'),
    mk('e-mp-mf0', 'demo-merge-p', 'out', 'demo-merge-f', 'in-0'),
    mk('e-w-s', 'demo-wait', 'out', 'demo-slack', 'in'),
    mk('e-s-mf1', 'demo-slack', 'out', 'demo-merge-f', 'in-1'),
    mk('e-mf-lg', 'demo-merge-f', 'out', 'demo-logger', 'in'),
    mk('e-lg-end', 'demo-logger', 'out', 'demo-end', 'in'),
  ];

  return { nodes, edges };
}

function mk(
  id: string,
  source: string,
  sourceHandle: string | null,
  target: string,
  targetHandle: string | null
): Edge {
  return {
    id,
    source,
    target,
    type: 'smoothstep',
    ...(sourceHandle ? { sourceHandle } : {}),
    ...(targetHandle ? { targetHandle } : {}),
  };
}
